import React, { useState, useEffect, useRef } from 'react';
import { authFetch } from '../services/api';
import './ApplicationDependencies.css';
import { useAuth } from '../context/AuthContext';

interface DeploymentStatusRecord {
  id: number;
  integration_group_id: string;
  application_id: string;
  deployment_status: 'Need Approval' | 'Approved' | 'In Development' | 'Deployed';
  changed_by_user: string | null;
  change_date: string | null;
  previous_status: string | null;
  change_reason: string | null;
  group_name: string | null;
  application_name: string | null;
  created_at: string | null;
  updated_at: string | null;
}

interface IntegrationGroup {
  integration_group_id: string;
  integration_group_description: string;
  application_count: number;
}

interface ApplicationOption {
  application_id: string;
  description: string;
  disposition?: string;
}

// Status colors
const getStatusColor = (status: string) => {
  switch (status) {
    case 'Need Approval': return { bg: '#fef3c7', color: '#92400e' };
    case 'Approved': return { bg: '#dbeafe', color: '#1e40af' };
    case 'In Development': return { bg: '#e0e7ff', color: '#3730a3' };
    case 'Deployed': return { bg: '#d1fae5', color: '#065f46' };
    default: return { bg: '#f5f5f5', color: '#757575' };
  }
};

// Status transition rules
const STATUS_TRANSITIONS: Record<string, string[]> = {
  'Need Approval': ['Approved'],
  'Approved': ['Need Approval', 'In Development'],
  'In Development': ['Deployed'],
  'Deployed': []
};

const DispositionControl: React.FC = () => {
  const { currentUser } = useAuth();
  const [records, setRecords] = useState<DeploymentStatusRecord[]>([]);
  const [integrationGroups, setIntegrationGroups] = useState<IntegrationGroup[]>([]);
  const [applications, setApplications] = useState<ApplicationOption[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [filterGroupingId, setFilterGroupingId] = useState<string>('');
  const [filterApplicationId, setFilterApplicationId] = useState<string>('');
  const [filterStatus, setFilterStatus] = useState<string>('');
  const [editingRecord, setEditingRecord] = useState<DeploymentStatusRecord | null>(null);
  const [viewingRecord, setViewingRecord] = useState<DeploymentStatusRecord | null>(null);
  const [showViewModal, setShowViewModal] = useState(false);
  const [showAppDropdown, setShowAppDropdown] = useState(false);
  const [formData, setFormData] = useState({
    integrationGroupId: '',
    applicationId: '',
    deploymentStatus: 'Need Approval' as string,
    changeReason: ''
  });

  const getUserFullName = () => {
    if (currentUser?.name) {
      return currentUser.name;
    } else if (currentUser?.email) {
      return currentUser.email;
    }
    return 'Unknown User';
  };

  useEffect(() => {
    fetchRecords();
    fetchIntegrationGroups();
  }, []);

  useEffect(() => {
    if (formData.integrationGroupId) {
      fetchApplicationsForGroup(formData.integrationGroupId);
    } else {
      setApplications([]);
    }
  }, [formData.integrationGroupId]);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (!target.closest('.custom-dropdown')) {
        setShowAppDropdown(false);
      }
    };
    document.addEventListener('click', handleClickOutside);
    return () => document.removeEventListener('click', handleClickOutside);
  }, []);

  const fetchRecords = async () => {
    try {
      const response = await authFetch('/api/deployment-status');
      if (!response.ok && response.status !== 404) {
        throw new Error('Failed to fetch');
      }
      const data = await response.json();
      if (Array.isArray(data)) {
        setRecords(data);
      } else {
        setRecords([]);
      }
      setLoading(false);
    } catch (err: any) {
      console.error('Error fetching deployment status records:', err);
      setRecords([]);
      setLoading(false);
    }
  };

  const fetchIntegrationGroups = async () => {
    try {
      const response = await authFetch('/api/application-dependencies/lookup/integration-groups');
      const result = await response.json();
      if (result.success) {
        setIntegrationGroups(result.data || []);
      }
    } catch (err) {
      console.error('Error loading integration groups:', err);
    }
  };

  const fetchApplicationsForGroup = async (groupId: string) => {
    try {
      const response = await authFetch(`/api/application-dependencies/lookup/applications/${groupId}`);
      const result = await response.json();
      if (result.success) {
        setApplications(result.data || []);
      }
    } catch (err) {
      console.error('Error loading applications:', err);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.integrationGroupId) {
      setError('Integration Group is required');
      return;
    }

    if (!formData.applicationId) {
      setError('Application is required');
      return;
    }

    try {
      if (editingRecord) {
        // Update existing record
        const response = await authFetch(`/api/deployment-status/${editingRecord.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            deployment_status: formData.deploymentStatus,
            changed_by_user: getUserFullName(),
            change_reason: formData.changeReason || null
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to update');
        }

        setSuccess('Deployment status updated successfully');
      } else {
        // Create new record
        const response = await authFetch('/api/deployment-status', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            integration_group_id: formData.integrationGroupId,
            application_id: formData.applicationId,
            deployment_status: formData.deploymentStatus,
            changed_by_user: getUserFullName(),
            change_reason: formData.changeReason || null
          })
        });

        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to create');
        }

        setSuccess('Deployment status record created successfully');
      }

      fetchRecords();
      handleCloseForm();
    } catch (err: any) {
      console.error('Failed to save deployment status:', err);
      setError(err.message || 'Failed to save deployment status');
    }
  };

  const handleEdit = (record: DeploymentStatusRecord) => {
    setEditingRecord(record);
    setFormData({
      integrationGroupId: record.integration_group_id,
      applicationId: record.application_id,
      deploymentStatus: record.deployment_status,
      changeReason: ''
    });
    setShowForm(true);
  };

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this deployment status record?')) {
      try {
        const response = await authFetch(`/api/deployment-status/${id}`, {
          method: 'DELETE'
        });
        if (!response.ok) {
          const errorData = await response.json();
          throw new Error(errorData.error || 'Failed to delete');
        }
        setSuccess('Deployment status record deleted successfully');
        fetchRecords();
      } catch (err: any) {
        setError(err.message || 'Failed to delete deployment status record');
      }
    }
  };

  const handleView = (record: DeploymentStatusRecord) => {
    setViewingRecord(record);
    setShowViewModal(true);
  };

  const handleCloseViewModal = () => {
    setViewingRecord(null);
    setShowViewModal(false);
  };

  const handleAddNew = () => {
    setEditingRecord(null);
    setFormData({
      integrationGroupId: '',
      applicationId: '',
      deploymentStatus: 'Need Approval',
      changeReason: ''
    });
    setShowForm(true);
  };

  const handleCloseForm = () => {
    setShowForm(false);
    setEditingRecord(null);
    setFormData({
      integrationGroupId: '',
      applicationId: '',
      deploymentStatus: 'Need Approval',
      changeReason: ''
    });
    setError('');
  };

  const formatDate = (dateString: string | null) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // Get unique grouping IDs from records for filter dropdown
  const uniqueGroupingIds = Array.from(new Set(records.map(r => r.integration_group_id).filter(Boolean))).sort();

  // Get application IDs filtered by selected grouping
  const filteredApplicationIds = Array.from(new Set(
    records
      .filter(r => !filterGroupingId || filterGroupingId === '__ALL__' || r.integration_group_id === filterGroupingId)
      .map(r => r.application_id)
      .filter(Boolean)
  )).sort();

  const filteredRecords = records.filter(record => {
    // If no grouping is selected, show no records
    if (!filterGroupingId) return false;
    // If "All Grouping IDs" is selected, show all groups
    const groupMatch = filterGroupingId === '__ALL__' || record.integration_group_id === filterGroupingId;
    const appMatch = !filterApplicationId || record.application_id === filterApplicationId;
    const statusMatch = !filterStatus || record.deployment_status === filterStatus;
    return groupMatch && appMatch && statusMatch;
  });

  const getAvailableTransitions = (currentStatus: string): string[] => {
    return STATUS_TRANSITIONS[currentStatus] || [];
  };

  return (
    <div className="page" style={{ height: '100vh', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <div className="page-header" style={{ position: 'sticky', top: 0, zIndex: 100, backgroundColor: 'white', flexShrink: 0 }}>
        <div className="page-header__content">
          <div className="page-header__title-section">
            <h1 className="page-header__title">Disposition Control</h1>
            <p className="page-header__subtitle">
              Manage deployment status for applications in integration groups
            </p>
          </div>
          <div className="page-header__actions">
            <button
              className="btn btn-primary"
              onClick={handleAddNew}
              title="Create new deployment status record"
            >
              + Add Status Record
            </button>
          </div>
        </div>
      </div>

      {loading && (
        <div className="md-loading" style={{ padding: '40px', textAlign: 'center', fontSize: '16px' }}>
          Loading deployment status records...
        </div>
      )}

      {!loading && (
        <>
          {/* Filter Section - Sticky below header */}
          <div style={{ flexShrink: 0, padding: '0 16px', marginBottom: '16px' }}>
            <div className="md-card" style={{ padding: '16px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '24px', flexWrap: 'wrap' }}>
                {/* Grouping ID Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '16px', fontWeight: '500', minWidth: '100px' }}>Grouping ID:</label>
                  <select
                    value={filterGroupingId}
                    onChange={(e) => {
                      setFilterGroupingId(e.target.value);
                      setFilterApplicationId(''); // Reset Application ID when Grouping changes
                    }}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px',
                      minWidth: '250px',
                      backgroundColor: 'white'
                    }}
                  >
                    <option value="">Select Grouping</option>
                    <option value="__ALL__">All Grouping IDs</option>
                    {uniqueGroupingIds.map(groupId => (
                      <option key={groupId} value={groupId}>{groupId}</option>
                    ))}
                  </select>
                </div>

                {/* Application ID Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '16px', fontWeight: '500', minWidth: '100px' }}>Application ID:</label>
                  <select
                    value={filterApplicationId}
                    onChange={(e) => setFilterApplicationId(e.target.value)}
                    disabled={!filterGroupingId}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px',
                      minWidth: '250px',
                      backgroundColor: !filterGroupingId ? '#f5f5f5' : 'white',
                      cursor: !filterGroupingId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">All Application IDs</option>
                    {filteredApplicationIds.map(appId => (
                      <option key={appId} value={appId}>{appId}</option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                  <label style={{ fontSize: '16px', fontWeight: '500', minWidth: '100px' }}>Status:</label>
                  <select
                    value={filterStatus}
                    onChange={(e) => setFilterStatus(e.target.value)}
                    disabled={!filterGroupingId}
                    style={{
                      padding: '8px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '4px',
                      fontSize: '16px',
                      minWidth: '200px',
                      backgroundColor: !filterGroupingId ? '#f5f5f5' : 'white',
                      cursor: !filterGroupingId ? 'not-allowed' : 'pointer'
                    }}
                  >
                    <option value="">All Statuses</option>
                    <option value="Need Approval">Need Approval</option>
                    <option value="Approved">Approved</option>
                    <option value="In Development">In Development</option>
                    <option value="Deployed">Deployed</option>
                  </select>
                </div>

                {/* Results counter */}
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginLeft: 'auto' }}>
                  <span style={{ fontSize: '16px', color: '#666' }}>
                    {!filterGroupingId
                      ? 'Select a Grouping to view data'
                      : `Showing ${filteredRecords.length} of ${records.length} records`
                    }
                  </span>
                  {filterGroupingId && (
                    <button
                      onClick={() => {
                        setFilterGroupingId('');
                        setFilterApplicationId('');
                        setFilterStatus('');
                      }}
                      style={{
                        padding: '6px 12px',
                        backgroundColor: '#2196f3',
                        color: 'white',
                        border: 'none',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        fontSize: '16px',
                        fontWeight: '500'
                      }}
                    >
                      Clear Filters
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Scrollable Data Content */}
          <div style={{ flex: 1, overflowY: 'auto', padding: '0 16px 16px 16px' }}>
            {/* Main Content Card */}
          <div className="md-card" style={{ overflow: 'visible', minHeight: 'auto' }}>
            {!filterGroupingId ? (
              <div className="md-empty-state" style={{ padding: '40px' }}>
                <div className="md-empty-icon">📋</div>
                <h3 style={{ fontSize: '16px' }}>Select a Grouping ID to View Data</h3>
                <p style={{ fontSize: '16px' }}>Please select a Grouping ID from the dropdown above to view deployment status records.</p>
              </div>
            ) : filteredRecords.length === 0 && records.length === 0 ? (
              <div className="md-empty-state" style={{ padding: '40px' }}>
                <div className="md-empty-icon">🚀</div>
                <h3 style={{ fontSize: '16px' }}>No Deployment Status Records Found</h3>
                <p style={{ fontSize: '16px' }}>Click "Add Status Record" to create deployment status tracking for applications</p>
              </div>
            ) : filteredRecords.length === 0 ? (
              <div className="md-empty-state" style={{ padding: '40px' }}>
                <div className="md-empty-icon">🔍</div>
                <h3 style={{ fontSize: '16px' }}>No Records Match Filters</h3>
                <p style={{ fontSize: '16px' }}>Try adjusting your filter criteria or clear filters to see all records.</p>
              </div>
            ) : (
              <div className="md-table-container" style={{ overflowY: 'visible', height: 'auto', maxHeight: 'none' }}>
                <table className="md-table" style={{ fontSize: '16px' }}>
                  <thead>
                    <tr>
                      <th style={{ textAlign: 'left', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>Grouping ID</th>
                      <th style={{ textAlign: 'left', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>Application ID</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>Deployment Status</th>
                      <th style={{ textAlign: 'left', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>Changed By</th>
                      <th style={{ textAlign: 'left', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>Change Date</th>
                      <th style={{ textAlign: 'center', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredRecords.map(record => (
                      <tr key={record.id}>
                        <td style={{ textAlign: 'left', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                          <div>
                            <div style={{ fontWeight: '500' }}>{record.integration_group_id}</div>
                            {record.group_name && (
                              <div style={{ fontSize: '14px', color: '#666' }}>{record.group_name}</div>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'left', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                          <div>
                            <div style={{ fontWeight: '500' }}>{record.application_id}</div>
                            {record.application_name && (
                              <div style={{ fontSize: '14px', color: '#666' }}>{record.application_name}</div>
                            )}
                          </div>
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '16px' }}>
                          <span style={{
                            display: 'inline-block',
                            padding: '6px 12px',
                            borderRadius: '16px',
                            fontSize: '16px',
                            fontWeight: '500',
                            backgroundColor: getStatusColor(record.deployment_status).bg,
                            color: getStatusColor(record.deployment_status).color
                          }}>
                            {record.deployment_status}
                          </span>
                        </td>
                        <td style={{ textAlign: 'left', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                          {record.changed_by_user || '-'}
                        </td>
                        <td style={{ textAlign: 'left', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                          {formatDate(record.change_date)}
                        </td>
                        <td style={{ textAlign: 'center', padding: '12px', fontSize: '16px', whiteSpace: 'nowrap' }}>
                          <div style={{ display: 'flex', gap: '4px', justifyContent: 'center', alignItems: 'center' }}>
                            <button
                              onClick={() => handleView(record)}
                              title="View details"
                              style={{
                                padding: '4px 6px',
                                backgroundColor: 'transparent',
                                color: '#1976d2',
                                border: '1px solid #1976d2',
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              👁️
                            </button>
                            <button
                              onClick={() => handleEdit(record)}
                              title="Edit status"
                              disabled={record.deployment_status === 'Deployed'}
                              style={{
                                padding: '4px 6px',
                                backgroundColor: record.deployment_status === 'Deployed' ? '#e0e0e0' : 'transparent',
                                color: record.deployment_status === 'Deployed' ? '#9e9e9e' : '#1976d2',
                                border: `1px solid ${record.deployment_status === 'Deployed' ? '#e0e0e0' : '#1976d2'}`,
                                borderRadius: '4px',
                                fontSize: '14px',
                                cursor: record.deployment_status === 'Deployed' ? 'not-allowed' : 'pointer',
                                minWidth: 'auto'
                              }}
                            >
                              ✏️
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          </div>

          {/* Form Modal */}
          {showForm && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '95%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
              }}>
                <div style={{
                  padding: '24px',
                  borderBottom: '3px solid #1976d2',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '500', color: '#212121' }}>
                    {editingRecord ? 'Update Deployment Status' : 'Add Deployment Status'}
                  </h2>
                  <button
                    onClick={handleCloseForm}
                    style={{
                      width: '32px',
                      height: '32px',
                      border: 'none',
                      background: 'transparent',
                      fontSize: '24px',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#757575'
                    }}
                  >
                    x
                  </button>
                </div>
                <div style={{ padding: '24px' }}>
                  <form onSubmit={handleSubmit}>
                    {error && (
                      <div style={{
                        marginBottom: '16px',
                        padding: '12px',
                        backgroundColor: '#fee2e2',
                        color: '#991b1b',
                        borderRadius: '4px',
                        fontSize: '16px'
                      }}>
                        {error}
                      </div>
                    )}

                    {/* Integration Group */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                        Integration Group *
                      </label>
                      <select
                        value={formData.integrationGroupId}
                        onChange={(e) => {
                          setFormData({
                            ...formData,
                            integrationGroupId: e.target.value,
                            applicationId: ''
                          });
                        }}
                        disabled={!!editingRecord}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: editingRecord ? '#f5f5f5' : 'white',
                          cursor: editingRecord ? 'not-allowed' : 'pointer'
                        }}
                        required
                      >
                        <option value="">Select Integration Group</option>
                        {integrationGroups.map(group => (
                          <option key={group.integration_group_id} value={group.integration_group_id}>
                            {group.integration_group_id} - {group.integration_group_description} ({group.application_count} apps)
                          </option>
                        ))}
                      </select>
                    </div>

                    {/* Application */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                        Application *
                      </label>
                      {editingRecord ? (
                        <input
                          type="text"
                          value={`${formData.applicationId}${editingRecord.application_name ? ` - ${editingRecord.application_name}` : ''}`}
                          disabled
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            fontSize: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            backgroundColor: '#f5f5f5',
                            cursor: 'not-allowed'
                          }}
                        />
                      ) : (
                        <div className="custom-dropdown" style={{ position: 'relative' }}>
                          <div
                            onClick={() => {
                              if (formData.integrationGroupId) {
                                setShowAppDropdown(!showAppDropdown);
                              }
                            }}
                            style={{
                              padding: '12px 16px',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              backgroundColor: !formData.integrationGroupId ? '#f5f5f5' : 'white',
                              cursor: formData.integrationGroupId ? 'pointer' : 'not-allowed',
                              fontSize: '16px',
                              display: 'flex',
                              justifyContent: 'space-between',
                              alignItems: 'center'
                            }}
                          >
                            <span style={{ color: formData.applicationId ? '#212121' : '#9e9e9e' }}>
                              {formData.applicationId && applications.find(a => a.application_id === formData.applicationId)
                                ? `${applications.find(a => a.application_id === formData.applicationId)!.application_id} - ${applications.find(a => a.application_id === formData.applicationId)!.description}`
                                : (formData.integrationGroupId ? 'Select Application' : 'Select Integration Group first')}
                            </span>
                            {formData.integrationGroupId && <span>▼</span>}
                          </div>
                          {showAppDropdown && formData.integrationGroupId && (
                            <div style={{
                              position: 'absolute',
                              top: '100%',
                              left: 0,
                              right: 0,
                              backgroundColor: 'white',
                              border: '1px solid #ddd',
                              borderRadius: '4px',
                              boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                              zIndex: 1000,
                              maxHeight: '200px',
                              overflowY: 'auto'
                            }}>
                              {applications.length === 0 ? (
                                <div style={{ padding: '12px 16px', color: '#9e9e9e', fontSize: '16px' }}>
                                  No applications available
                                </div>
                              ) : (
                                applications.map(app => (
                                  <div
                                    key={app.application_id}
                                    onClick={() => {
                                      setFormData({ ...formData, applicationId: app.application_id });
                                      setShowAppDropdown(false);
                                    }}
                                    style={{
                                      padding: '12px 16px',
                                      cursor: 'pointer',
                                      fontSize: '16px',
                                      borderBottom: '1px solid #f0f0f0',
                                      backgroundColor: formData.applicationId === app.application_id ? '#e3f2fd' : 'white'
                                    }}
                                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#f5f5f5')}
                                    onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = formData.applicationId === app.application_id ? '#e3f2fd' : 'white')}
                                  >
                                    <strong>{app.application_id}</strong> - {app.description}
                                  </div>
                                ))
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Deployment Status */}
                    <div style={{ marginBottom: '20px' }}>
                      <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                        Deployment Status *
                      </label>
                      <select
                        value={formData.deploymentStatus}
                        onChange={(e) => setFormData({ ...formData, deploymentStatus: e.target.value })}
                        style={{
                          width: '100%',
                          padding: '12px 16px',
                          fontSize: '16px',
                          border: '1px solid #ddd',
                          borderRadius: '4px',
                          backgroundColor: 'white',
                          cursor: 'pointer'
                        }}
                        required
                      >
                        {editingRecord ? (
                          // Show only valid transitions when editing
                          <>
                            <option value={editingRecord.deployment_status}>{editingRecord.deployment_status} (current)</option>
                            {getAvailableTransitions(editingRecord.deployment_status).map(status => (
                              <option key={status} value={status}>{status}</option>
                            ))}
                          </>
                        ) : (
                          // Show all options when creating
                          <>
                            <option value="Need Approval">Need Approval</option>
                            <option value="Approved">Approved</option>
                            <option value="In Development">In Development</option>
                            <option value="Deployed">Deployed</option>
                          </>
                        )}
                      </select>
                      {editingRecord && (
                        <p style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>
                          Allowed transitions from "{editingRecord.deployment_status}": {getAvailableTransitions(editingRecord.deployment_status).join(', ') || 'None'}
                        </p>
                      )}
                    </div>

                    {/* Change Reason (only for edits) */}
                    {editingRecord && formData.deploymentStatus !== editingRecord.deployment_status && (
                      <div style={{ marginBottom: '20px' }}>
                        <label style={{ display: 'block', fontSize: '16px', fontWeight: '500', marginBottom: '8px' }}>
                          Change Reason
                        </label>
                        <textarea
                          value={formData.changeReason}
                          onChange={(e) => setFormData({ ...formData, changeReason: e.target.value })}
                          placeholder="Optional: Provide a reason for this status change"
                          style={{
                            width: '100%',
                            padding: '12px 16px',
                            fontSize: '16px',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            minHeight: '80px',
                            resize: 'vertical'
                          }}
                        />
                      </div>
                    )}

                    <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                      <button
                        type="button"
                        onClick={handleCloseForm}
                        style={{
                          padding: '12px 24px',
                          fontSize: '16px',
                          fontWeight: '500',
                          border: '1px solid #1976d2',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: 'transparent',
                          color: '#1976d2'
                        }}
                      >
                        Cancel
                      </button>
                      <button
                        type="submit"
                        style={{
                          padding: '12px 24px',
                          fontSize: '16px',
                          fontWeight: '500',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          backgroundColor: '#1976d2',
                          color: 'white'
                        }}
                      >
                        {editingRecord ? 'Update Status' : 'Create Status Record'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>
          )}

          {/* View Modal */}
          {showViewModal && viewingRecord && (
            <div style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 9999
            }}>
              <div style={{
                backgroundColor: 'white',
                borderRadius: '8px',
                maxWidth: '600px',
                width: '95%',
                maxHeight: '90vh',
                overflow: 'auto',
                boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)'
              }}>
                <div style={{
                  padding: '24px',
                  borderBottom: '3px solid #1976d2',
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center'
                }}>
                  <h2 style={{ margin: 0, fontSize: '20px', fontWeight: '500', color: '#212121' }}>
                    Deployment Status Details
                  </h2>
                  <button
                    onClick={handleCloseViewModal}
                    style={{
                      width: '32px',
                      height: '32px',
                      border: 'none',
                      background: 'transparent',
                      fontSize: '24px',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: '#757575'
                    }}
                  >
                    x
                  </button>
                </div>
                <div style={{ padding: '24px' }}>
                  <div style={{ display: 'grid', gap: '16px' }}>
                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Integration Group ID</label>
                        <div style={{ fontSize: '16px', fontWeight: '500' }}>{viewingRecord.integration_group_id}</div>
                        {viewingRecord.group_name && (
                          <div style={{ fontSize: '14px', color: '#666' }}>{viewingRecord.group_name}</div>
                        )}
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Application ID</label>
                        <div style={{ fontSize: '16px', fontWeight: '500' }}>{viewingRecord.application_id}</div>
                        {viewingRecord.application_name && (
                          <div style={{ fontSize: '14px', color: '#666' }}>{viewingRecord.application_name}</div>
                        )}
                      </div>
                    </div>

                    <div>
                      <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Current Deployment Status</label>
                      <span style={{
                        display: 'inline-block',
                        padding: '6px 12px',
                        borderRadius: '16px',
                        fontSize: '16px',
                        fontWeight: '500',
                        backgroundColor: getStatusColor(viewingRecord.deployment_status).bg,
                        color: getStatusColor(viewingRecord.deployment_status).color
                      }}>
                        {viewingRecord.deployment_status}
                      </span>
                    </div>

                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Changed By</label>
                        <div style={{ fontSize: '16px' }}>{viewingRecord.changed_by_user || '-'}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Change Date</label>
                        <div style={{ fontSize: '16px' }}>{formatDate(viewingRecord.change_date)}</div>
                      </div>
                    </div>

                    {viewingRecord.previous_status && (
                      <div>
                        <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Previous Status</label>
                        <div style={{ fontSize: '16px' }}>{viewingRecord.previous_status}</div>
                      </div>
                    )}

                    {viewingRecord.change_reason && (
                      <div>
                        <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Change Reason</label>
                        <div style={{ fontSize: '16px', padding: '8px', backgroundColor: '#f5f5f5', borderRadius: '4px' }}>
                          {viewingRecord.change_reason}
                        </div>
                      </div>
                    )}

                    <div style={{ display: 'flex', gap: '16px' }}>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Created At</label>
                        <div style={{ fontSize: '16px' }}>{formatDate(viewingRecord.created_at)}</div>
                      </div>
                      <div style={{ flex: 1 }}>
                        <label style={{ fontSize: '14px', color: '#666', display: 'block', marginBottom: '4px' }}>Last Updated</label>
                        <div style={{ fontSize: '16px' }}>{formatDate(viewingRecord.updated_at)}</div>
                      </div>
                    </div>
                  </div>

                  <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '20px', marginTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                    <button
                      onClick={handleCloseViewModal}
                      style={{
                        padding: '12px 24px',
                        fontSize: '16px',
                        fontWeight: '500',
                        border: '1px solid #1976d2',
                        borderRadius: '4px',
                        cursor: 'pointer',
                        backgroundColor: 'transparent',
                        color: '#1976d2'
                      }}
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Success/Error Messages */}
          {success && (
            <div className="md-toast md-toast--success" style={{ fontSize: '16px' }}>
              {success}
              <button onClick={() => setSuccess('')}>x</button>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DispositionControl;
